import { buildTenantAdminCapabilityList } from '../config/channel-feature-flags.config';

import type { RoleChannelAccess, ChannelFeatureFlag } from '../slice/channelFeatureFlagApi';

function familyOf(capabilityKey: string): string {
  const i = capabilityKey.indexOf(':');
  return i > 0 ? capabilityKey.slice(0, i) : capabilityKey;
}

function flagsMatchingChannel(
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  ch: string
): ChannelFeatureFlag[] {
  return tenantChannelFlags.filter(
    (f) => f.capabilityKey === ch || (!ch.includes(':') && f.channel === ch)
  );
}

/**
 * `platformBlocked` from API for UI:
 * - **Composite key** (`EMAIL:GOOGLE`): blocked if that row is blocked (same as backend).
 * - **Family key** (`EMAIL`): blocked only when **every** tenant flag row for that family is
 *   platform-blocked. If any provider row is not blocked, the parent row should not look
 *   “locked” while a child shows available — matches “any provider allowed ⇒ family not fully locked”.
 */
export function platformBlockedForCapabilityKey(
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  ch: string
): boolean {
  const relevant = flagsMatchingChannel(tenantChannelFlags, ch);
  if (!relevant.length) return false;
  if (ch.includes(':')) {
    return relevant.some((f) => f.platformBlocked);
  }
  return relevant.every((f) => f.platformBlocked);
}

/** `enabled` from API only: at least one matching row is on (no implicit "on" when no row). */
export function tenantEnabledForCapabilityKey(
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  ch: string
): boolean {
  return flagsMatchingChannel(tenantChannelFlags, ch).some((f) => f.enabled);
}

function denySetFromRows(rows: readonly RoleChannelAccess[]): Set<string> {
  const s = new Set<string>();
  rows.forEach((row) => {
    if (row.isEnabled === false) s.add(row.channel);
  });
  return s;
}

/** Role deny on a family (e.g. EMAIL) applies to composite keys (EMAIL:GOOGLE). */
export function roleDenyAppliesToCapability(denySet: Set<string>, channel: string): boolean {
  if (denySet.has(channel)) return true;
  if (!channel.includes(':')) return false;
  return denySet.has(familyOf(channel));
}

function isCapabilityDeniedByRole(denySet: Set<string>, channel: string): boolean {
  return roleDenyAppliesToCapability(denySet, channel);
}

/** Same effective "on" as the role Channel access matrix for one capability key. */
export function isChannelCapabilityAllowedForRole(
  channel: string,
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  denySet: Set<string>,
  isSuperAdmin: boolean
): boolean {
  const denied = isCapabilityDeniedByRole(denySet, channel);
  const platformBlocked = platformBlockedForCapabilityKey(tenantChannelFlags, channel);
  const tenantOn = tenantEnabledForCapabilityKey(tenantChannelFlags, channel);

  const isParent = !channel.includes(':');
  const subFlags = isParent
    ? tenantChannelFlags.filter((f) => f.capabilityKey.startsWith(`${channel}:`))
    : [];
  const hasSub = isParent && subFlags.length > 0;

  const allSubChannelsDenied =
    hasSub &&
    subFlags.every((f) => {
      const subKey = f.capabilityKey;
      const subDenied = roleDenyAppliesToCapability(denySet, subKey);
      const subPlatformBlocked = platformBlockedForCapabilityKey(tenantChannelFlags, subKey);
      const subTenantEnabled = tenantEnabledForCapabilityKey(tenantChannelFlags, subKey);
      const subOn = !subDenied && subTenantEnabled && !subPlatformBlocked;
      return !subOn;
    });

  const allSubChannelsDisabled =
    hasSub &&
    subFlags.every((f) => {
      const subKey = f.capabilityKey;
      const subPlatformBlocked = platformBlockedForCapabilityKey(tenantChannelFlags, subKey);
      const subTenantEnabled = tenantEnabledForCapabilityKey(tenantChannelFlags, subKey);
      const subOn = subTenantEnabled && !subPlatformBlocked;
      return !subOn;
    });

  const effectiveDenied = denied || allSubChannelsDenied;

  if (isSuperAdmin) {
    return tenantOn && !platformBlocked && !allSubChannelsDisabled;
  }
  return !effectiveDenied && !platformBlocked;
}

/**
 * Families (WHATSAPP, EMAIL, ...) where at least one capability row would be "on"
 * in the role dialog — used so Create Channel step 1 matches that matrix.
 */
export function familiesWithAnyAllowedCapability(
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  roleAccessRows: readonly RoleChannelAccess[] | undefined,
  isSuperAdmin: boolean
): Set<string> {
  const accessRows = isSuperAdmin ? [] : (roleAccessRows ?? []);
  const displayChannels = buildTenantAdminCapabilityList(tenantChannelFlags, accessRows);
  const denySet = isSuperAdmin ? new Set<string>() : denySetFromRows(roleAccessRows ?? []);

  const out = new Set<string>();
  for (const ch of displayChannels) {
    if (isChannelCapabilityAllowedForRole(ch, tenantChannelFlags, denySet, isSuperAdmin)) {
      out.add(familyOf(ch));
    }
  }
  return out;
}

/** `emailStep1Data` item `value` → tenant/role capability key (CHANNEL_FLAGGING_FRONTEND_API.md). */
export function emailProviderUiValueToCapabilityKey(providerValue: string): string {
  const raw = providerValue.trim();
  if (raw === 'other_provider') return 'EMAIL:OTHER';
  return `EMAIL:${raw.toUpperCase()}`;
}

/** Per-provider card in Create Channel → Email step 2 vs role matrix. */
export function isEmailProviderAllowedForRole(
  providerUiValue: string,
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  roleAccessRows: readonly RoleChannelAccess[] | undefined,
  isSuperAdmin: boolean
): boolean {
  const cap = emailProviderUiValueToCapabilityKey(providerUiValue);
  const denySet = isSuperAdmin ? new Set<string>() : denySetFromRows(roleAccessRows ?? []);
  return isChannelCapabilityAllowedForRole(cap, tenantChannelFlags, denySet, isSuperAdmin);
}

/** `smsStep1Data` item `value` (e.g. twilio, bms_api) → capability key `SMS:TWILIO`, `SMS:BMS_API`. */
export function smsProviderUiValueToCapabilityKey(providerValue: string): string {
  const raw = providerValue.trim().toLowerCase().replace(/-/g, '_');
  if (!raw) return 'SMS';
  return `SMS:${raw.toUpperCase()}`;
}

/** Per-provider card in Create Channel → SMS step 2 vs role matrix (e.g. role row `SMS:BMS_API`). */
export function isSmsProviderAllowedForRole(
  providerUiValue: string,
  tenantChannelFlags: readonly ChannelFeatureFlag[],
  roleAccessRows: readonly RoleChannelAccess[] | undefined,
  isSuperAdmin: boolean
): boolean {
  const cap = smsProviderUiValueToCapabilityKey(providerUiValue);
  const denySet = isSuperAdmin ? new Set<string>() : denySetFromRows(roleAccessRows ?? []);
  return isChannelCapabilityAllowedForRole(cap, tenantChannelFlags, denySet, isSuperAdmin);
}
